import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useEffect, useMemo } from 'react';
import { getGameTheme } from './games.js';
const ThemeContext = createContext(null);
/**
 * Provides game-specific theming to the app.
 * Injects CSS custom properties for theme colors.
 */
export function ThemeProvider({ children, gameType }) {
    const theme = useMemo(() => getGameTheme(gameType), [gameType]);
    // Inject CSS custom properties for theme colors
    useEffect(() => {
        const root = document.documentElement;
        // Set color variables
        Object.entries(theme.colors).forEach(([key, value]) => {
            // Convert camelCase to kebab-case for CSS
            const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
            root.style.setProperty(`--theme-${cssKey}`, value);
        });
        // Set gradient variables (these are Tailwind classes, stored for reference)
        root.style.setProperty('--theme-gradient-bg', theme.gradients.background);
        root.style.setProperty('--theme-gradient-card', theme.gradients.card);
        // Set theme ID for CSS selectors
        root.setAttribute('data-theme', theme.id);
        return () => {
            // Cleanup on unmount
            Object.keys(theme.colors).forEach((key) => {
                const cssKey = key.replace(/([A-Z])/g, '-$1').toLowerCase();
                root.style.removeProperty(`--theme-${cssKey}`);
            });
            root.style.removeProperty('--theme-gradient-bg');
            root.style.removeProperty('--theme-gradient-card');
            root.removeAttribute('data-theme');
        };
    }, [theme]);
    const contextValue = useMemo(() => ({ theme, gameType }), [theme, gameType]);
    return _jsx(ThemeContext.Provider, { value: contextValue, children: children });
}
/**
 * Hook to access the current theme
 */
export function useTheme() {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
}
/**
 * Hook to get theme-aware class names
 */
export function useThemeClasses() {
    const { theme } = useTheme();
    return {
        /** Background gradient classes */
        bgGradient: `bg-gradient-to-br ${theme.gradients.background}`,
        /** Card gradient classes */
        cardGradient: `bg-gradient-to-br ${theme.gradients.card}`,
        /** Primary color style */
        primaryColor: { color: theme.colors.primary },
        /** Primary background style */
        primaryBg: { backgroundColor: theme.colors.primary },
        /** Accent color style */
        accentColor: { color: theme.colors.accent },
        /** Surface background style */
        surfaceBg: { backgroundColor: theme.colors.surface },
    };
}
//# sourceMappingURL=ThemeProvider.js.map